import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  MapPin,
  ArrowRight,
  TrendingUp,
  Users,
  Target,
  Megaphone,
  ChevronDown,
  ExternalLink,
  Sparkles,
  AlertCircle,
  Loader2,
  Youtube,
  Image as ImageIcon,
  Play,
  X,
  Music2,
  Facebook,
  User,
  Eye
} from 'lucide-react';
import { cn } from '@utils/cn';
import { newsService } from '@/services/news.service';
import type { NewsItem } from '@/types';

// -- helper: detect YouTube video ID from a URL --
function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|v\/))([^#&?]{11})/
  );
  return match ? match[1] : null;
}

function isYouTubeUrl(url: string): boolean {
  return /(?:youtu\.be|youtube\.com)/.test(url);
}

// -- helper: detect TikTok video ID from a URL --
function getTikTokId(url: string): string | null {
  // Matches: tiktok.com/@user/video/1234567890123456789
  const match = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  // Matches: tiktok.com/@user/photo/1234567890123456789
  const match2 = url.match(/tiktok\.com\/@[^/]+\/photo\/(\d+)/);
  return match ? match[1] : match2 ? match2[1] : null;
}

function isTikTokUrl(url: string): boolean {
  return /(?:tiktok\.com|vm\.tiktok\.com)/.test(url);
}

// ───────────────────── Facebook helpers ─────────────────────

/** Return true for any Facebook URL */
function isFacebookUrl(url: string): boolean {
  return /(?:facebook\.com|fb\.com|fb\.watch|web\.facebook\.com|m\.facebook\.com|www\.facebook\.com)/.test(url);
}

/** Try to extract a Facebook profile/page from the URL so we can show it.
 *  Returns e.g. "dr.rui.araujo" or "pages/dr.rui.araujo/..."  */
function getFacebookDisplay(url: string): { page: string | null; isProfile: boolean } {
  // facebook.com/<name>  or  facebook.com/profile.php?id=<id>
  let m = url.match(/facebook\.com\/profile\.php\?id=([^&]+)/);
  if (m) return { page: m[1], isProfile: true };

  m = url.match(/facebook\.com\/([^/?#]+)/i);
  if (m) {
    const skip = ['plugins','login','sharer','watch','groups','events','hashtag',
      'marketplace','help','privacy','business','reel','photo','video','live'];
    if (!skip.includes(m[1])) return { page: m[1], isProfile: true };
  }
  return { page: null, isProfile: false };
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const categoryConfig: Record<string, { color: string; bg: string; icon: any; label: string }> = {
  announcement: {
    color: 'text-red-600',
    bg: 'bg-red-100',
    icon: Megaphone,
    label: 'Announcement'
  },
  milestone: {
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    icon: TrendingUp,
    label: 'Milestone'
  },
  event: {
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    icon: Users,
    label: 'Event'
  },
  story: {
    color: 'text-emerald-600',
    bg: 'bg-emerald-100',
    icon: Target,
    label: 'Story'
  },
  update: {
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    icon: MapPin,
    label: 'Update'
  }
};

export function News() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedNews, setExpandedNews] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(4);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [tiktokVideoUrl, setTiktokVideoUrl] = useState<string | null>(null);
  const [facebookUrl, setFacebookUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const apiResponse = await newsService.getAll();
        setNewsItems(apiResponse || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, []);

  const filteredNews = selectedCategory === 'all'
    ? newsItems
    : newsItems.filter(news => news.category === selectedCategory);

  const visibleNews = filteredNews.slice(0, visibleCount);
  const hasMore = visibleCount < filteredNews.length;

  const categories = ['all', ...Object.keys(categoryConfig)];

  const getCategoryIcon = (category: string) => {
    const config = categoryConfig[category];
    return config ? config.icon : Megaphone;
  };

  const getCategoryLabel = (category: string) => {
    if (category === 'all') return t('news.categories.all', 'All News');
    return categoryConfig[category]?.label || category;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-700 via-sky-500 to-sky-400 text-white pt-32 pb-24">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-40 right-1/3 w-64 h-64 bg-blue-300/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-sky-300" />
              <span className="text-sm font-semibold uppercase tracking-wider">{t('news.badge', 'Stay Updated')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-black mb-6 leading-tight">
              {/* {t('news.title', 'Latest News')} */}
              Tempo To'o Ona
            </h1>
            <p className="text-xl md:text-2xl font-light max-w-3xl mx-auto opacity-90 leading-relaxed">
              {t('news.subtitle', 'Follow our journey as we build a better future for Timor-Leste together')}
            </p>
            
            {/* Stats badges */}
            <div className="flex flex-wrap justify-center gap-4 mt-12">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl">
                <div className="text-3xl font-black">{newsItems.length}</div>
                <div className="text-sm font-medium opacity-80">{t('news.stats.updates', 'Updates')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl">
                <div className="text-3xl font-black">{Object.keys(categoryConfig).length}</div>
                <div className="text-sm font-medium opacity-80">{t('news.stats.categories', 'Categories')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl">
                <div className="text-3xl font-black">2026</div>
                <div className="text-sm font-medium opacity-80">{t('news.stats.year', 'Year')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L48 10C96 20 192 40 288 53.3C384 67 480 73 576 68.3C672 63 768 47 864 42C960 37 1056 43 1152 50C1248 57 1344 63 1392 66.7L1440 70V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z" fill="url(#gradient)" />
            <defs>
              <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="100" gradientUnits="userSpaceOnUse">
                <stop stopColor="#f0f9ff" />
                <stop offset="1" stopColor="white" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">{t('news.filter', 'Filter by')}:</span>
            {categories.map((category) => {
              const isActive = selectedCategory === category;
              const Icon = getCategoryIcon(category);

              return (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setVisibleCount(4);
                  }}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap',
                    isActive
                      ? 'bg-sky-600 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {getCategoryLabel(category)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* News Timeline */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {isLoading ? (
          <div className="text-center py-16">
            <Loader2 className="w-12 h-12 mx-auto text-sky-500 animate-spin mb-4" />
            <h3 className="text-2xl font-heading font-bold text-gray-700 mb-2">
              {t('news.loading.title', 'Loading data...')}
            </h3>
            <p className="text-gray-500">
              {t('news.loading.message', 'Please wait while we fetch the latest updates...')}
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="text-red-400 mb-4">
              <AlertCircle className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-gray-700 mb-2">
              {t('news.error.title', 'Failed to Load Data')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('news.error.message', 'We encountered an error while fetching the latest data.')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-heading font-bold px-6 py-3 rounded-full transition-all"
            >
              {t('news.error.retry', 'Try Again')}
            </button>
          </div>
        ) : visibleNews.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <Megaphone className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-gray-700 mb-2">
              {t('news.empty.title', 'No News Found')}
            </h3>
            <p className="text-gray-500">
              {t('news.empty.message', 'No data items available in this category yet.')}
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-sky-300 via-sky-400 to-transparent hidden lg:block" />
            
            <div className="space-y-12">
              {visibleNews.map((news, index) => {
                const isExpanded = expandedNews === news.id;
                const config = categoryConfig[news.category] || categoryConfig.campaign;
                const CategoryIcon = config.icon;
                const isLeft = index % 2 === 0;
                
                return (
                  <div
                    key={news.id}
                    className={cn(
                      'relative flex items-stretch',
                      'lg:flex-row',
                      isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    )}
                    style={{
                      animationDelay: `${index * 0.1}s`
                    }}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 z-10 hidden lg:flex items-center justify-center">
                      <div className={cn(
                        'w-12 h-12 rounded-full flex items-center justify-center shadow-lg',
                        config.bg,
                        config.color
                      )}>
                        <CategoryIcon className="w-6 h-6" />
                      </div>
                    </div>
                    
                    {/* Content Card */}
                    <div className={cn(
                      'w-full lg:w-1/2',
                      isLeft ? 'lg:pr-16' : 'lg:pl-16'
                    )}>
                      <article
                        className={cn(
                          'group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer',
                          'border border-gray-100',
                          isExpanded && 'ring-2 ring-sky-400'
                        )}
                        onClick={() => setExpandedNews(isExpanded ? null : news.id)}
                      >
                        {/* Media section - images / YouTube / TikTok / Facebook */}
                        {(() => {
                          const ytId = news.imageUrl && isYouTubeUrl(news.imageUrl) ? getYouTubeId(news.imageUrl) : null;
                          const tiktokId = news.imageUrl && isTikTokUrl(news.imageUrl) ? getTikTokId(news.imageUrl) : null;
                          const isFb = news.imageUrl && isFacebookUrl(news.imageUrl);
                          const hasImages = news.images && news.images.length > 0;
                          const hasCoverImage = news.imageUrl && !ytId && !tiktokId && !isFb;

                          if (isExpanded && (ytId || tiktokId || isFb || hasImages || hasCoverImage)) {
                            return (
                              <div className="px-6 lg:px-8 pt-4">
                                {/* YouTube embed */}
                                {ytId && (
                                  <div className="rounded-xl overflow-hidden mb-4 aspect-video">
                                    <iframe
                                      src={`https://www.youtube.com/embed/${ytId}?rel=0`}
                                      title={news.title}
                                      className="w-full h-full"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  </div>
                                )}

                                {/* TikTok iframe embed */}
                                {tiktokId && (
                                  <div className="rounded-xl overflow-hidden mb-4 bg-gray-50">
                                    <iframe
                                      src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
                                      title={news.title}
                                      className="w-full rounded-xl"
                                      style={{ minHeight: '600px', maxWidth: '340px', margin: '0 auto', display: 'block' }}
                                      frameBorder="0"
                                      allow="encrypted-media"
                                    />
                                  </div>
                                )}

                                {/* Facebook embed */}
                                {isFb && (
                                  <div className="rounded-xl overflow-hidden mb-4 border border-gray-200 bg-white">
                                    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                                        <Facebook className="w-5 h-5 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 truncate">{news.title}</p>
                                        <a
                                          href={news.imageUrl!}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:underline truncate block"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          View on Facebook
                                        </a>
                                      </div>
                                    </div>
                                    <div className="p-3" style={{ minHeight: '200px' }}>
                                      <iframe
                                        src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(news.imageUrl!)}`}
                                        className="w-full border-0"
                                        style={{ minHeight: '400px' }}
                                        scrolling="no"
                                        frameBorder="0"
                                        allow="encrypted-media"
                                        allowFullScreen
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Facebook page link (when URL points to a page/profile) */}
                                {isFb && (() => {
                                  const fb = getFacebookDisplay(news.imageUrl!);
                                  if (fb.page) {
                                    return (
                                      <div className="mb-4">
                                        <a
                                          href={news.imageUrl!}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group/fb"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                                            {fb.isProfile ? (
                                              <User className="w-6 h-6 text-white" />
                                            ) : (
                                              <Facebook className="w-6 h-6 text-white" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {fb.page}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {fb.isProfile ? 'Facebook Profile' : 'Facebook Page'}
                                            </p>
                                          </div>
                                          <ExternalLink className="w-4 h-4 text-gray-400 group-hover/fb:text-blue-600 transition-colors flex-shrink-0" />
                                        </a>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}

                                {/* Image gallery */}
                                {hasImages && (
                                  <>
                                    {!ytId && !tiktokId && !isFb && <p className="text-sm font-semibold text-gray-600 mb-2">Images</p>}
                                    <div className={cn(
                                      'grid gap-3 mb-4',
                                      news.images!.length === 1 ? 'grid-cols-1' :
                                      news.images!.length === 2 ? 'grid-cols-2' :
                                      news.images!.length === 3 ? 'grid-cols-3' :
                                      'grid-cols-2'
                                    )}>
                                      {news.images!.map((img, imgIdx) => (
                                        <div
                                          key={imgIdx}
                                          className="relative rounded-xl overflow-hidden aspect-video cursor-pointer group/img hover:scale-[1.02] transition-transform"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setLightboxImage(img);
                                          }}
                                        >
                                          <img
                                            src={img}
                                            alt={`${news.title} - image ${imgIdx + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                          />
                                          <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
                                            <ImageIcon className="w-6 h-6 text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}

                                {/* Single cover image (when no gallery or video) */}
                                {hasCoverImage && !ytId && !tiktokId && !isFb && !hasImages && (
                                  <div
                                    className="relative rounded-xl overflow-hidden aspect-video mb-4 cursor-pointer group/img hover:scale-[1.02] transition-transform"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLightboxImage(news.imageUrl!);
                                    }}
                                  >
                                    <img
                                      src={news.imageUrl}
                                      alt={news.title}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
                                      <ImageIcon className="w-6 h-6 text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // ─────────── Collapsed state thumbnails ───────────

                          if (ytId) {
                            return (
                              <div className="relative aspect-video overflow-hidden">
                                <img
                                  src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                                  alt={news.title}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                                    <Play className="w-8 h-8 text-white ml-1" />
                                  </div>
                                </div>
                                <div className="absolute top-4 left-4">
                                  <span className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                                    <Youtube className="w-3 h-3" />
                                    YouTube
                                  </span>
                                </div>
                              </div>
                            );
                          }

                          if (tiktokId) {
                            return (
                              <>
                                <div
                                  className="relative aspect-video overflow-hidden bg-black/10 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTiktokVideoUrl(news.imageUrl!);
                                  }}
                                >
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                      <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center shadow-lg mx-auto mb-2">
                                        <Music2 className="w-8 h-8 text-white" />
                                      </div>
                                      <span className="text-xs text-gray-400">Tap to view TikTok</span>
                                    </div>
                                  </div>
                                  <div className="absolute top-4 left-4">
                                    <span className="inline-flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-bold">
                                      <Music2 className="w-3 h-3" />
                                      TikTok
                                    </span>
                                  </div>
                                </div>

                                {tiktokVideoUrl === news.imageUrl && (
                                  <div
                                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
                                    onClick={() => setTiktokVideoUrl(null)}
                                  >
                                    <button
                                      className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
                                      onClick={() => setTiktokVideoUrl(null)}
                                    >
                                      <X className="w-8 h-8" />
                                    </button>
                                    <div
                                      className="bg-black rounded-2xl overflow-hidden flex flex-col max-h-[90vh] w-full max-w-sm p-2"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {(() => {
                                        const tkId = getTikTokId(tiktokVideoUrl!);
                                        return tkId ? (
                                          <iframe
                                            src={`https://www.tiktok.com/embed/v2/${tkId}`}
                                            title="TikTok"
                                            className="w-full rounded-xl"
                                            style={{ minHeight: '600px' }}
                                            frameBorder="0"
                                            allow="encrypted-media"
                                          />
                                        ) : (
                                          <div className="text-white text-center p-8">
                                            <Music2 className="w-12 h-12 mx-auto mb-4 opacity-60" />
                                            <p className="text-sm text-gray-400">Unable to play this TikTok video.</p>
                                            <a
                                              href={tiktokVideoUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 mt-4 text-blue-300 hover:text-blue-200 text-sm"
                                            >
                                              <ExternalLink className="w-4 h-4" />
                                              Open in TikTok
                                            </a>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          }

                          {/* Facebook thumbnail */}
                          if (isFb) {
                            const fb = getFacebookDisplay(news.imageUrl!);
                            return (
                              <>
                                <div
                                  className="relative aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer border border-blue-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFacebookUrl(news.imageUrl!);
                                  }}
                                >
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                      <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center shadow-lg mx-auto mb-2">
                                        <Eye className="w-8 h-8 text-white" />
                                      </div>
                                      <span className="text-xs font-medium text-blue-700">Tap to view on Facebook</span>
                                    </div>
                                  </div>
                                  {fb.page && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent px-4 py-2">
                                      <div className="flex items-center gap-2 text-white">
                                        <Facebook className="w-4 h-4" />
                                        <span className="text-sm font-semibold truncate">{fb.page}</span>
                                      </div>
                                    </div>
                                  )}
                                  <div className="absolute top-4 left-4">
                                    <span className="inline-flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                                      <Facebook className="w-3 h-3" />
                                      Facebook
                                    </span>
                                  </div>
                                </div>

                                {/* Facebook modal */}
                                {facebookUrl === news.imageUrl && (
                                  <div
                                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
                                    onClick={() => setFacebookUrl(null)}
                                  >
                                    <button
                                      className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
                                      onClick={() => setFacebookUrl(null)}
                                    >
                                      <X className="w-8 h-8" />
                                    </button>
                                    <div
                                      className="bg-white rounded-2xl overflow-hidden flex flex-col max-h-[90vh] w-full max-w-2xl"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <iframe
                                        src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(news.imageUrl!)}`}
                                        className="w-full border-0"
                                        style={{ minHeight: '500px', maxHeight: 'calc(90vh - 60px)' }}
                                        scrolling="no"
                                        frameBorder="0"
                                        allow="encrypted-media"
                                        allowFullScreen
                                      />
                                      <div className="p-3 bg-gray-50 border-t flex items-center justify-between">
                                        <span className="text-sm text-gray-500 truncate">Viewing on Facebook</span>
                                        <a
                                          href={news.imageUrl!}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                          Open on Facebook
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          }

                          if (hasImages && news.images![0]) {
                            return (
                              <div className="relative aspect-video overflow-hidden">
                                <img
                                  src={news.images![0]}
                                  alt={news.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                {news.images!.length > 1 && (
                                  <div className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                    <ImageIcon className="w-3 h-3" />
                                    +{news.images!.length - 1}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          if (hasCoverImage) {
                            return (
                              <div className="relative aspect-video overflow-hidden">
                                <img
                                  src={news.imageUrl}
                                  alt={news.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            );
                          }

                          return null;
                        })()}

                        {/* Category badge */}
                        <div className="absolute top-4 right-4 z-10">
                          <span className={cn(
                            'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold',
                            config.bg,
                            config.color
                          )}>
                            <CategoryIcon className="w-3 h-3" />
                            {config.label}
                          </span>
                        </div>

                        {/* Card content */}
                        <div className="p-6 lg:p-8">
                          {/* Date */}
                          <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                            <Calendar className="w-4 h-4" />
                            <time dateTime={news.publishedDate}>
                              {formatDate(news.publishedDate)}
                            </time>
                          </div>

                          {/* Title */}
                          <h3 className="text-2xl font-heading font-black text-gray-900 mb-3 group-hover:text-sky-600 transition-colors leading-tight">
                            {news.title}
                          </h3>

                          {/* Excerpt */}
                          <p className={cn(
                            'text-gray-600 leading-relaxed',
                            isExpanded ? '' : 'line-clamp-3'
                          )}>
                            {news.excerpt}
                          </p>

                          {/* Read more indicator */}
                          <div className="mt-6 flex items-center gap-2 text-sky-600 font-semibold text-sm">
                            <span>{isExpanded ? t('news.showLess', 'Show Less') : t('news.readMore', 'Read Full Story')}</span>
                            <ArrowRight className={cn(
                              'w-4 h-4 transition-transform',
                              isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'
                            )} />
                          </div>
                        </div>

                        {/* Gradient overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                      </article>
                    </div>

                    {/* Empty space for other side */}
                    <div className="hidden lg:block w-1/2" />
                  </div>
                );
              })}
            </div>

            {/* Load more button */}
            {hasMore && (
              <div className="text-center mt-16">
                <button
                  onClick={() => setVisibleCount(prev => prev + 4)}
                  className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-heading font-bold px-8 py-4 rounded-full transition-all hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <span>{t('news.loadMore', 'Load More Updates')}</span>
                  <ChevronDown className="w-5 h-5 animate-bounce" />
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-sky-900 to-blue-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-black mb-6">
            {t('news.cta.title', 'Be Part of the Story')}
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            {t('news.cta.description', 'Join our movement and help us create positive change across Timor-Leste. Your voice matters, your vote counts.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/volunteer"
              className="inline-flex items-center gap-2 bg-white text-sky-900 font-heading font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all hover:scale-105"
            >
              <Users className="w-5 h-5" />
              <span>{t('news.cta.volunteer', 'Join as Volunteer')}</span>
            </a>
            <a
              href="/about"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white font-heading font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-all"
            >
              <span>{t('news.cta.learnMore', 'Learn More')}</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
